import React, { useEffect, useState, useRef } from 'react';
import BackWithSidebar from '../../../../components/BackWithSidebar';
import GeneralIcon from '../../../../assets';
import storeProfileService from '../../../../services/storeProfileService';
import { mapStoreProfileFromApi } from '../../../../models/storeProfileModel';
import imageStore from '../../../../services/imageStore';
import { buscarCEP } from '../../../../services/cepService';
import { useMask } from '../../../../hooks/useMask';

function Modal({ open, onClose, children, title }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-4">

        {/* Header do modal com seta + título */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-100"
          >
            ←
          </button>

          <h2 className="text-lg font-semibold text-gray-800">
            {title}
          </h2>
        </div>

        {/* Conteúdo */}
        {children}
      </div>
    </div>
  );
}

export default function PerfilLoja() {
  const [tab, setTab] = useState('sobre'); // 'sobre' | 'contato' | 'imagens'
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);

  const [showSobreEdit, setShowSobreEdit] = useState(false);
  const [showContatoEdit, setShowContatoEdit] = useState(false);

  const [sobreForm, setSobreForm] = useState({ name: '', category: '', email: '', code: '', about: '' });
  const [contatoForm, setContatoForm] = useState({ supportEmail: '', phone: '', address: { cep: '', country: '', state: '', city: '', district: '', street: '', number: '', complement: '' } });

  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [cepLoading, setCepLoading] = useState(false);

  // Mask hook
  const { applyMask } = useMask();

// remove stray input fragment


  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await storeProfileService.fetchProfile();
      setProfile(mapStoreProfileFromApi(data));
      const url = await storeProfileService.getLogoUrl();
      setLogoUrl(url);
    } catch (e) {
      console.error('fetchProfile failed', e);
    } finally { setLoading(false); }
  }

  async function handleAddImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    await storeProfileService.addMedia(file);
    await load();
  }

  async function handleRemoveImage(id) {
    await storeProfileService.removeMedia(id);
    await load();
  }

  async function handleChangeLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    await storeProfileService.updateLogo(file);
    const url = await storeProfileService.getLogoUrl();
    setLogoUrl(url);
    await load();
  }

  if (loading || !profile) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <div className="text-center py-16 text-gray-500">Carregando...</div>
      </div>
    );
  }

  const contact = profile.contact || {};
  const addr = contact.address || {};

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <BackWithSidebar to="/dashboard" className="p-2 rounded-full hover:bg-gray-100" />
        <h2 className="text-lg font-semibold">Perfil da loja</h2>
      </div>

      {/* Header with icon / logo */}
      <div className="flex flex-col items-center mb-6">
        <div className="w-40 h-40 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-full h-full object-cover" />
          ) : (
            <GeneralIcon iconName="loja" className="w-8 h-8 text-purple-600" />
          )}
        </div>
        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleChangeLogo} />
        <div className="mt-3 text-center">
          <div className="text-xl font-semibold">{profile.name}</div>
          <div className="text-sm text-gray-600">{profile.category}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 justify-center mb-6">
        {['sobre','contato','imagens'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full border ${tab===t ? 'bg-purple-600 text-white border-purple-600' : 'bg-gray-100 text-gray-700 border-gray-200'}`}
          >
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded p-4 shadow-sm">
        {tab === 'sobre' && (
          <div className="space-y-4">
            <div className="text-sm font-semibold mb-2">Geral</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <div className="text-xs text-gray-600">Nome fantasia</div>
                <div className="text-gray-900">{profile.name}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Principal categoria de atuação</div>
                <div className="text-gray-900">{profile.category || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Email comercial</div>
                <div className="text-gray-900">{profile.email || 'Não cadastrado.'}</div>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs text-gray-600">Código da loja</div>
              <div className="flex items-center gap-2">
                <input value={profile.code || ''} readOnly className="bg-gray-100 border rounded px-3 py-2 text-sm" />
                <button
                  onClick={() => navigator.clipboard.writeText(profile.code || '')}
                  className="p-2 rounded border hover:bg-gray-100 bg-white"
                  title="Copiar código"
                >
                  <GeneralIcon iconName="copy" className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Sobre</div>
              <div className="text-gray-800 text-sm leading-relaxed whitespace-pre-line">
                {profile.about || 'Não cadastrado.'}
              </div>
            </div>

            <div className="mt-6">
              <button onClick={() => {
                setSobreForm({ name: profile.name || '', category: profile.category || '', email: profile.email || '', code: profile.code || '', about: profile.about || '' });
                setShowSobreEdit(true);
              }} className="w-full py-3 rounded bg-purple-600 text-white font-medium">Editar informações</button>
            </div>
          </div>
        )}

        {tab === 'contato' && (
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-600">Email de suporte ao cliente</div>
              <div className="text-gray-900">{contact.supportEmail || 'Não cadastrado.'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Telefone de contato</div>
              <div className="text-gray-900">{contact.phone || 'Não cadastrado.'}</div>
            </div>

            <div className="text-sm font-semibold mt-4">Endereço</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <div className="text-xs text-gray-600">CEP</div>
                <div className="text-gray-900">{addr.cep || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">País</div>
                <div className="text-gray-900">{addr.country || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Estado</div>
                <div className="text-gray-900">{addr.state || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Cidade</div>
                <div className="text-gray-900">{addr.city || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Bairro</div>
                <div className="text-gray-900">{addr.district || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Rua</div>
                <div className="text-gray-900">{addr.street || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Número</div>
                <div className="text-gray-900">{addr.number || 'Não cadastrado.'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Complemento</div>
                <div className="text-gray-900">{addr.complement || 'Não cadastrado.'}</div>
              </div>
            </div>

           

            <div className="mt-10">
              <button onClick={() => {
                setContatoForm({
                  supportEmail: contact.supportEmail || '',
                  phone: contact.phone || '',
                  address: {
                    cep: addr.cep || '', country: addr.country || '', state: addr.state || '', city: addr.city || '',
                    district: addr.district || '', street: addr.street || '', number: addr.number || '', complement: addr.complement || ''
                  }
                });
                setShowContatoEdit(true);
              }} className="w-full py-3 rounded bg-purple-600 text-white font-medium">Editar informações</button>
            </div>
          </div>
        )}

        {tab === 'imagens' && (
          <div>
            <div className="text-sm font-semibold mb-3">Fotos e vídeos</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(profile.media || []).length === 0 && (
                <div className="col-span-2 sm:col-span-3 flex flex-col items-center justify-center py-12 text-gray-500">
                  <GeneralIcon iconName="floresta" className="w-30 h-30 mb-2" />
                  Nenhuma imagem cadastrada.
                </div>
              )}
              {(profile.media || []).map(m => (
                <div key={m.id} className="relative group">
                  {/* resolve URL from key */}
                  <MediaImage keyProp={m.key} />
                  <button
                    onClick={() => handleRemoveImage(m.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded opacity-90"
                    title="Excluir"
                  >
                    <GeneralIcon iconName="trash" className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 rounded bg-purple-600 text-white font-medium"
              >
                + Adicionar uma imagem
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAddImage} />
            </div>
          </div>
        )}
      </div>

      {/* Edit Sobre Modal */}


<Modal open={showSobreEdit} onClose={() => setShowSobreEdit(false)} title="Geral">
  <div className="flex flex-col gap-4">

    {/* Logo preview + upload dashed box */}
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
        {logoUrl ? (
          <img src={logoUrl} alt="logo" className="w-full h-full object-cover" />
        ) : (
          <GeneralIcon iconName="loja" className="w-20 h-20 text-purple-600" />
        )}
      </div>
      <button
        type="button"
        onClick={() => logoInputRef.current?.click()}
        className="flex-1 border-2 border-dashed border-purple-300 rounded-xl py-4 text-center hover:bg-purple-50"
      >
        <div className="flex items-center justify-center gap-2 text-purple-600">
          <GeneralIcon iconName="camera" className="w-10 h-10" />
          Alterar a imagem
        </div>
      </button>
      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleChangeLogo} />
    </div>

    {/* Campo Nome Fantasia */}
    <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
      Nome fantasia
      <input
        value={sobreForm.name}
        onChange={e => setSobreForm(s => ({ ...s, name: e.target.value }))}
        className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </label>

    {/* Categoria */}
    <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
      Principal categoria de atuação
      <input
        value={sobreForm.category}
        onChange={e => setSobreForm(s => ({ ...s, category: e.target.value }))}
        className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </label>

    {/* Email Comercial */}
    <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
      Email comercial
      <input
        value={sobreForm.email}
        onChange={e => setSobreForm(s => ({ ...s, email: e.target.value }))}
        className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </label>

    {/* Código personalizado */}
    <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
      Código personalizado da loja
      <div className="flex items-center gap-2">
        <input
          value={sobreForm.code}
          onChange={e => setSobreForm(s => ({ ...s, code: e.target.value }))}
          className="flex-1 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          className="px-3 py-2 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700"
          onClick={() => setSobreForm(s => ({ ...s, code: s.code }))}
        >
          Gerar
        </button>
      </div>
    </label>

    {/* Descrição */}
    <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
      Descrição da loja
      <textarea
        value={sobreForm.about}
        onChange={e => setSobreForm(s => ({ ...s, about: e.target.value }))}
        rows={5}
        className="px-3 py-2 rounded-xl border border-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </label>

    {/* Botão atualizar */}
    <button
      onClick={async () => {
        await storeProfileService.updateProfile({
          name: sobreForm.name,
          category: sobreForm.category,
          email: sobreForm.email,
          code: sobreForm.code,
          about: sobreForm.about,
        });
        setShowSobreEdit(false);
        await load();
      }}
      className="w-full py-3 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700"
    >
      Atualizar
    </button>
  </div>
</Modal>


{/* Edit Contato Modal */}

<Modal open={showContatoEdit} onClose={() => setShowContatoEdit(false)} title="Contato">
  <div className="max-h-[80vh] overflow-y-auto p-6">
    <div className="flex flex-col gap-5">

      {/* Email de suporte */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Email de suporte ao cliente
        <input
          value={contatoForm.supportEmail}
          onChange={e =>
            setContatoForm(s => ({
              ...s,
              supportEmail: applyMask("email", e.target.value)
            }))
          }
          className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="empresaexemplo@suporte.com"
        />
      </label>

      {/* Telefone */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Telefone de contato
        <input
          value={contatoForm.phone}
          onChange={e =>
            setContatoForm(s => ({
              ...s,
              phone: applyMask("phone", e.target.value)
            }))
          }
          className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="(99) 99999-9999"
        />
      </label>

      {/* Endereço */}
      <h3 className="text-sm font-semibold text-gray-700">Endereço</h3>

      {/* CEP + botão de busca */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        CEP
        <div className="flex items-center gap-2">
          <input
            value={contatoForm.address.cep}
            onChange={e =>
              setContatoForm(s => ({
                ...s,
                address: {
                  ...s.address,
                  cep: applyMask("cep", e.target.value)
                }
              }))
            }
            className="flex-1 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="12345-678"
          />

          <button
            type="button"
            onClick={async () => {
              const cep = contatoForm.address.cep || '';
              try {
                setCepLoading(true);
                const data = await buscarCEP(cep);

                setContatoForm(s => ({
                  ...s,
                  address: {
                    ...s.address,
                    cep: data.cep || s.address.cep,
                    country: 'Brasil',
                    state: data.uf || s.address.state,
                    city: data.localidade || s.address.city,
                    district: data.bairro || s.address.district,
                    street: data.logradouro || s.address.street,
                    complement: data.complemento || s.address.complement,
                  }
                }));
              } catch (err) {
                alert(err?.message || 'Erro ao buscar CEP');
              } finally {
                setCepLoading(false);
              }
            }}
            className="p-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center"
            disabled={!!(contatoForm.address.cep === '') || cepLoading}
            title="Buscar CEP"
          >
            {cepLoading ? (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
              </svg>
            ) : (
              <GeneralIcon iconName="search" className="w-4 h-4" />
            )}
          </button>
        </div>
      </label>

      {/* País */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        País
        <select
          value={contatoForm.address.country}
          onChange={e => setContatoForm(s => ({
            ...s,
            address: { ...s.address, country: e.target.value }
          }))}
          className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="Brasil">Brasil</option>
        </select>
      </label>

      {/* Estado */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Estado*
        <input
          value={contatoForm.address.state}
          onChange={e => setContatoForm(s => ({
            ...s,
            address: { ...s.address, state: e.target.value }
          }))}
          className="px-3 py-2 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </label>

      {/* Cidade */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Cidade
        <input
          value={contatoForm.address.city}
          onChange={e =>
            setContatoForm(s => ({
              ...s,
              address: { ...s.address, city: e.target.value }
            }))
          }
          className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </label>

      {/* Bairro */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Bairro
        <input
          value={contatoForm.address.district}
          onChange={e =>
            setContatoForm(s => ({
              ...s,
              address: { ...s.address, district: e.target.value }
            }))
          }
          className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </label>

      {/* Rua + Número */}
      <div className="grid grid-cols-3 gap-3">
        <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium col-span-2">
          Rua
          <input
            value={contatoForm.address.street}
            onChange={e =>
              setContatoForm(s => ({
                ...s,
                address: { ...s.address, street: e.target.value }
              }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
          Número
          <input
            value={contatoForm.address.number}
            onChange={e =>
              setContatoForm(s => ({
                ...s,
                address: {
                  ...s.address,
                  number: applyMask("number", e.target.value)
                }
              }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </label>
      </div>

      {/* Complemento */}
      <label className="flex flex-col gap-1 text-sm text-gray-700 font-medium">
        Complemento
        <input
          value={contatoForm.address.complement}
          onChange={e =>
            setContatoForm(s => ({
              ...s,
              address: { ...s.address, complement: e.target.value }
            }))
          }
          className="px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </label>

      {/* Botão Atualizar */}
      <button
        onClick={async () => {
          await storeProfileService.updateProfile({
            contact: {
              supportEmail: contatoForm.supportEmail,
              phone: contatoForm.phone,
              address: { ...contatoForm.address },
            },
          });
          setShowContatoEdit(false);
          await load();
        }}
        className="w-full py-3 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700"
      >
        Atualizar
      </button>
    </div>
  </div>
</Modal>






    </div>
  );
}

function MediaImage({ keyProp }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const u = keyProp ? await imageStore.getImageUrl(keyProp) : null;
      if (mounted) setUrl(u);
    })();
    return () => { mounted = false; };
  }, [keyProp]);
  return <img src={url || ''} alt="media" className="w-full h-32 object-cover rounded bg-gray-100" />;
}
